import {
  LauncherCampaignsResponseSchema,
  fetchLauncherCampaigns,
  type Campaign as LauncherCampaign,
} from '../_shared/launcher-campaign-schema';

/**
 * Cross-app read of launcher's campaign list (Context: "Launcher HTTP
 * reads" — `GET /api/tools/launcher/campaigns`).
 *
 * Extracted out of `components/Home.tsx` and `components/ScanReview.tsx` —
 * both declared this same schema/type/mapper independently before this file
 * existed. `components/ScansPage.tsx` needed a third copy of the exact same
 * parse+map step, which is the point this app's promotion convention exists
 * for: promote at the 3rd copy (`mini-apps/CLAUDE.md`). Same pattern as
 * `campaign-members.ts` in this app.
 *
 * QA round 8, U2 — two changes here, both about the react-query CACHE rather
 * than about this app's own screens:
 *
 *  1. The response is parsed with `_shared/launcher-campaign-schema.ts`'s
 *     `LauncherCampaignsResponseSchema`, not with a slim local one. Lead
 *     Scanner only reads five columns, but the row it validates has to be the
 *     same row launcher/playbooks/playhouse validate, or the app can no longer
 *     tell a real launcher response from something else.
 *  2. The mapped result is cached under this app's OWN key (below), never
 *     under launcher's. Caching the slim `Campaign` view model under
 *     `['launcher','campaigns']` is what crashed Playbooks and Playhouse on a
 *     soft-nav out of Lead Scanner: they read that key expecting the full
 *     launcher row and called `member_count.toLocaleString()` on a row that
 *     had no `member_count`.
 */
export interface Campaign {
  id: string;
  name: string;
  eventDate: string | null;
  city: string | null;
  state: string | null;
}

function toLeadScannerCampaigns(rows: LauncherCampaign[]): Campaign[] {
  return rows.map(
    (c): Campaign => ({
      id: c.id,
      name: c.name,
      eventDate: c.event_date,
      city: c.city ?? null,
      state: c.state ?? null,
    }),
  );
}

/** Launcher's campaign list, fetched through `_shared`'s one launcher fetcher and mapped to this app's view model. */
export async function fetchCampaigns(apiFetch: Parameters<typeof fetchLauncherCampaigns>[0]): Promise<Campaign[]> {
  return toLeadScannerCampaigns(await fetchLauncherCampaigns(apiFetch));
}

/** Kept for `components/ScansPage.tsx`, which open PR #1595 also edits; switch it to `fetchCampaigns` and delete this once #1595 lands.
 *  Parses and maps a raw `/campaigns` response body. Throws if the response
 *  doesn't match the expected shape (caller decides how to surface that —
 *  same convention as every other `safeParse` boundary in this app). */
export function mapLauncherCampaignsResponse(json: unknown): Campaign[] {
  const parsed = LauncherCampaignsResponseSchema.safeParse(json);
  if (!parsed.success) throw new Error(`Invalid /campaigns response: ${parsed.error.message}`);
  return toLeadScannerCampaigns(parsed.data);
}

/** `campaignWhenAndWhere()` — the prototype-derived "date · city, state"
 *  summary line used everywhere a campaign is shown in a combobox option or
 *  a "Filed under" summary. */
export function campaignWhenAndWhere(c: Campaign): string {
  const parts = [c.eventDate, [c.city, c.state].filter(Boolean).join(', ')].filter(Boolean);
  return parts.join(' · ');
}

/**
 * Lead Scanner's OWN cache entry for the mapped campaign list — `Home.tsx`,
 * `ScanReview.tsx`, `ScansPage.tsx` and `RepEventMapper.tsx` share it so
 * react-query fires one request across every surface that shows a campaign
 * picker.
 *
 * It is deliberately NOT `['launcher','campaigns']`: the value stored here is
 * this app's slim `Campaign` view model, and that key belongs to the full
 * launcher row (`_shared/launcher-campaign-schema.ts`). Anything that should
 * refresh launcher's copy too invalidates BOTH keys — see
 * `RepEventMapper.tsx`'s "Sync events from Salesforce" mutation.
 */
export const LEAD_SCANNER_CAMPAIGNS_QUERY_KEY = ['lead-scanner', 'launcher-campaigns'] as const;
